import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import Barcode from "react-barcode";
import { QRCodeCanvas } from "qrcode.react";

// CSS
import "./BarcodeScannerComponent.css";

// Components
import InputField from "./inputField";
import Button from "./button";

//Functions and hooks
import { useNotification } from "../../Contexts/NotificationContext";
import { usePopup } from "../../Contexts/PopupContext";
import { useTranslation } from "react-i18next";

const BarcodeScannerComponent = ({
  onScanSuccess,
  className = "w-100%",
  scanBtnText = "Scan Barcode",
  showCodeBtnText = "Show Code",
  code,
  onClose,
  enableShowCode = true,
  btnSize = "btn-small",
}) => {
  const scannerRef = useRef(null);
  const [cameraId, setCameraId] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [scanning, setScanning] = useState(false);
  const isScanning = useRef(false);
  const hasNotified = useRef(false);
  const { showNotification } = useNotification();
  const { showPopup, removePopup, clearAllPopups } = usePopup();
  const { t } = useTranslation();

  // Add cleanup on component unmount
  useEffect(() => {
    return () => {
      safelyStopScanner();
    };
  }, []);

  const openScanner = () => {
    const key = Date.now();
    showPopup(
      <div className="barcode-overlay">
        <div className="barcode-scanner-container">
          {cameras.length > 1 && (
            <InputField
              label={t("Select Camera:")}
              value={cameraId}
              onChange={handleCameraChange}
              type="dropdown"
              options={cameras.map((cam) => ({
                value: cam.id,
                label: cam.label || t(`Camera ${cam.id}`),
              }))}
              className="mb-3"
            />
          )}

          <div
            id="custom-reader"
            className="custom-reader"
            style={{ width: "100%", marginTop: "1rem" }}
          />
          <Button
            btnStyle={"btn-negative"}
            btnSize={"btn-small"}
            onClick={() => {
              safelyStopScanner().then(() => {
                setScanning(false);
                removePopup(key);
              });
            }}
            className="mt-2"
          >
            {t("Close")}
          </Button>
        </div>
      </div>,
      key
    );
  };

  const openCodeView = () => {
    const key = Date.now();
    showPopup(
      <div className="barcode-overlay">
        <div className="barcode-scanner-container">
          {isLikelyBarcode(code) ? (
            <Barcode value={code} />
          ) : (
            <QRCodeCanvas value={code} size={150} />
          )}
          <Button
            btnStyle={"btn-secondary"}
            btnSize={"btn-small"}
            onClick={() => {
              closePopup(key);
            }}
            className="mt-3"
          >
            {t("Close")}
          </Button>
        </div>
      </div>,
      key
    );
  };

  const ClearPopup = () => {
    console.log("Clearing popups");
    clearAllPopups();
  };

  useEffect(() => {
    if (!scanning) return;

    // Add a small delay to ensure previous scanner is fully cleaned up
    const initializeCamera = async () => {
      try {
        // Ensure any existing scanner is stopped first
        await safelyStopScanner();

        // Small delay to let resources free up
        await new Promise((resolve) => setTimeout(resolve, 100));

        const devices = await Html5Qrcode.getCameras();

        if (!devices || devices.length === 0) {
          if (!hasNotified.current) {
            showNotification(t("No cameras found on this device."), "error");
            hasNotified.current = true;
          }
          ClearPopup();
          setScanning(false);
        } else {
          devices.reverse();
          setCameras(devices);
          setCameraId(devices[0].id);
        }
      } catch (err) {
        console.error("Camera initialization error:", err);
        if (!hasNotified.current) {
          showNotification(
            t(
              "Error accessing camera. Please check permissions and try closing other camera applications."
            ),
            "error"
          );
          hasNotified.current = true;
        }
        ClearPopup();
        setScanning(false);
      }
    };

    initializeCamera();
  }, [scanning]);

  useEffect(() => {
    if (cameraId && scanning) {
      const initializeScanner = async () => {
        try {
          await safelyStopScanner();

          await new Promise((resolve) => setTimeout(resolve, 200));

          const html5QrCode = new Html5Qrcode("custom-reader");
          scannerRef.current = html5QrCode;

          const config = {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            videoConstraints: {
              facingMode: "environment", // Try rear camera first
            },
          };

          await html5QrCode.start(
            cameraId,
            config,
            (decodedText) => {
              if (isScanning.current) {
                if (navigator.vibrate) {
                  navigator.vibrate(100);
                }
                onScanSuccess(decodedText);
                ClearPopup();
                safelyStopScanner().then(() => setScanning(false));
              }
            },
            () => {} // Error callback for failed scans (not critical)
          );

          isScanning.current = true;
        } catch (err) {
          console.error("Scanner start error:", err);

          let errorMessage = t("Failed to start scanner.");

          if (err.name === "NotReadableError") {
            errorMessage = t(
              "Camera is busy or unavailable. Please close other camera applications and try again."
            );
          } else if (err.name === "NotAllowedError") {
            errorMessage = t(
              "Camera permission denied. Please allow camera access and try again."
            );
          } else if (err.name === "NotFoundError") {
            errorMessage = t(
              "No camera found. Please ensure a camera is connected."
            );
          }

          showNotification(errorMessage, "error");
          setScanning(false);
          ClearPopup();
        }
      };

      initializeScanner();

      return () => {
        safelyStopScanner();
      };
    }
  }, [cameraId, scanning]);

  const safelyStopScanner = async () => {
    const scanner = scannerRef.current;
    if (scanner && isScanning.current) {
      console.log("Stopping scanner...");
      try {
        isScanning.current = false;

        if (scanner.getState() === Html5Qrcode.STATE_SCANNING) {
          await scanner.stop();
        }
        await scanner.stop();
        await scanner.clear();

        await new Promise((resolve) => setTimeout(resolve, 100));

        ClearPopup();
      } catch (err) {
        console.error("Error stopping scanner:", err);
        try {
          await scanner.clear();
        } catch (clearErr) {
          console.error("Error clearing scanner:", clearErr);
        }
      } finally {
        scannerRef.current = null;
      }
    }
  };

  const handleCameraChange = async (value) => {
    // Stop current scanner before switching
    await safelyStopScanner();
    setCameraId(value);
  };

  const isLikelyBarcode = (value) => {
    return /^\d{8,13}$/.test(value);
  };

  const handleShowCode = () => {
    if (!code || !enableShowCode) return;
    openCodeView();
  };

  const closePopup = (key) => {
    removePopup(key);
  };

  return (
    <div className={"barcode-component " + className}>
      <div className="btn-container justify-end items-end w-100% max-w-none">
        {enableShowCode ? (
          <Button
            btnStyle="btn-secondary"
            btnSize={btnSize}
            className="mb-1"
            onClick={handleShowCode}
          >
            {t(showCodeBtnText)}
          </Button>
        ) : null}

        <Button
          btnStyle="btn-secondary"
          btnSize={btnSize}
          className="mb-1"
          onClick={() => {
            // Reset notification flag
            hasNotified.current = false;
            setScanning(true);
            openScanner();
          }}
        >
          {t(scanBtnText)}
        </Button>
      </div>
    </div>
  );
};

export default BarcodeScannerComponent;
